import React, { createContext, useContext, useState, useEffect } from 'react';
import { ModuleProgress, ProgressState, ContentType, ModuleType } from '../types/progress';
import { useAuth } from './AuthContext';
import { GoogleDriveService } from '../services/googleDriveService';

interface ProgressContextType {
  progress: ProgressState;
  markAsCompleted: (moduleType: ModuleType, contentType: ContentType, id: string) => void;
  markAsIncomplete: (moduleType: ModuleType, contentType: ContentType, id: string) => void;
  getProgress: (moduleType: ModuleType, contentType: ContentType) => string[];
  getCompletionPercentage: (moduleType: ModuleType) => {
    theory: number;
    practice: number;
    video: number;
    overall: number;
  };
  resetAllProgress: () => void;
  isSyncing: boolean;
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

const STORAGE_KEY = 'algoFlowProgress';

export const ProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state from localStorage
  const [progress, setProgress] = useState<ProgressState>(() => {
    try {
      const savedProgress = localStorage.getItem(STORAGE_KEY);
      if (savedProgress) {
        return JSON.parse(savedProgress);
      }
    } catch (error) {
      console.error('Error loading progress from localStorage:', error);
    }
    return {};
  });

  const [isSyncing, setIsSyncing] = useState(false);
  const { isAuthenticated } = useAuth();
  const driveService = GoogleDriveService.getInstance();

  // Load from Google Drive when authenticated
  useEffect(() => {
    const loadFromDrive = async () => {
      if (!isAuthenticated) return;
      
      setIsSyncing(true);
      try {
        const driveProgress = await driveService.loadProgress();
        if (driveProgress) {
          setProgress(prevProgress => ({
            ...prevProgress,
            ...driveProgress
          }));
        }
      } catch (error) {
        console.error('Error loading from Drive:', error);
      } finally {
        setIsSyncing(false);
      }
    };

    loadFromDrive();
  }, [isAuthenticated]);

  // Save to localStorage whenever progress changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress to localStorage:', error);
    }
  }, [progress]);

  // Save to Google Drive when authenticated and progress changes
  useEffect(() => {
    if (!isAuthenticated) return;

    const saveToGDrive = async () => {
      setIsSyncing(true);
      try {
        await driveService.saveProgress(progress);
      } catch (error) {
        console.error('Error saving to Drive:', error);
      } finally {
        setIsSyncing(false);
      }
    };

    // Debounce save to Drive to prevent too many API calls
    const timeoutId = setTimeout(saveToGDrive, 1000);
    return () => clearTimeout(timeoutId);
  }, [progress, isAuthenticated]);

  const markAsCompleted = (moduleType: ModuleType, contentType: ContentType, id: string) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      if (!newProgress[moduleType]) {
        newProgress[moduleType] = {
          theory: [],
          practice: [],
          video: []
        };
      }
      if (!newProgress[moduleType][contentType].includes(id)) {
        newProgress[moduleType][contentType] = [...newProgress[moduleType][contentType], id];
      }
      return newProgress;
    });
  };

  const markAsIncomplete = (moduleType: ModuleType, contentType: ContentType, id: string) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      if (newProgress[moduleType]?.[contentType]) {
        newProgress[moduleType][contentType] = newProgress[moduleType][contentType].filter(
          item => item !== id
        );
      }
      return newProgress;
    });
  };

  const getProgress = (moduleType: ModuleType, contentType: ContentType): string[] => {
    return progress[moduleType]?.[contentType] || [];
  };

  const getCompletionPercentage = (moduleType: ModuleType) => {
    const moduleProgress = progress[moduleType] || {
      theory: [],
      practice: [],
      video: []
    };
    
    const theory = moduleProgress.theory.length || 0;
    const practice = moduleProgress.practice.length || 0;
    const video = moduleProgress.video.length || 0;

    return {
      theory: theory > 0 ? 100 : 0,
      practice: practice > 0 ? 100 : 0,
      video: video > 0 ? 100 : 0,
      overall: Math.round(((theory > 0 ? 1 : 0) + (practice > 0 ? 1 : 0) + (video > 0 ? 1 : 0)) * 33.33)
    };
  };

  const resetAllProgress = () => {
    setProgress({});
    localStorage.removeItem(STORAGE_KEY);
    
    if (isAuthenticated) {
      setIsSyncing(true);
      driveService.deleteProgress()
        .catch(error => console.error('Error deleting progress from Google Drive:', error))
        .finally(() => setIsSyncing(false));
    }
  };

  return (
    <ProgressContext.Provider value={{
      progress,
      markAsCompleted,
      markAsIncomplete,
      getProgress,
      getCompletionPercentage,
      resetAllProgress,
      isSyncing
    }}>
      {children}
    </ProgressContext.Provider>
  );
};
